import onScrollDone from './scroll-end-listener';
import HeaderHeightCalculator from './header-height-calculator';
import ScrollUpdateChecker from './scroll-update-checker';

/**
 * Replacement for scrollIntoView with default center position, and optional top/bottom.
 * @param  {DOMElement} link Element to be scrolled to.
 * @param  {string} position Top or bottom position.
 */

/**
 * Provides functions for scrolling within the document while accommodating
 * fixed headings of various sizes.
 */
 export default class DocumentScroller {

    /**
     * @param {ScrollUpdateChecker } scrollUpdateChecker provides coordination
     * of scroll updates so that certain listeners can pause while a Javascript-triggered
     * scroll is in progress
     * @param  {HeaderHeightCalculator} headerHeightCalculator calculates header height
    */
    constructor(scrollUpdateChecker, headerHeightCalculator) {
      this.scrollUpdateChecker = scrollUpdateChecker;
      this.headerHeightCalculator = headerHeightCalculator;
    }

    /**
     * @returns how far the page is currently scrolled, in pixels
     */
    getCurrentScroll() {
      return window.pageYOffset;
    }

    /**
     *
     * @param {number} desiredPosition the position to which the caller would like
     * to scroll
     * @returns additional offset required to scroll to that position from document's
     * current position and not have it overlap
     */
    getSituationalOffsetFromPos(desiredPosition) {
      return this.getSituationalOffset(desiredPosition > this.getCurrentScroll());
    }

    /**
     *
     * @param {boolean} isMovingDown true if the scroll triggering this calculation
     *  will be moving down, false if it will be moving up.
     * @returns additional offset required to up or down from document's
     * current position and not have the desired position overlap headers.
     *
     * As of current state,
     * not all browsers return up-to-date information while scrolling, so 1 is returned.  The function
     * is left in place for future enhancement.
     */
    getSituationalOffset(isMovingDown) {
      return 1;
    }

    /**
     * @param { DOMRect } rect rectangle to scroll to
     * @param { string } position desired alignment of rect on screen. Values: 'top', 'bottom', 'minimum-onscreen' (gets the element on screen
     * with minimal amount of scrolling).  Other values, including null, will return the adjustment to align rect in the
     * middle of the screen
     * @returns the adjustment, in pixels, required to scroll to rect with alignment to position.
     *
     * */
    calculateAdjustment(rect, position) {

      const windowHeight = window.innerHeight;
      const headerHeight = this.headerHeightCalculator.getFullHeaderHeight();
      const noScrollResult = { needsScroll: false, adjustment: 0 };

      function getScrollResult(adjustment) {
        return { needsScroll: true, adjustment: adjustment };
      }

      const readabilityAdjustment = 20;

      function getTopAdjustment() {
        return headerHeight * -1;
      }

      function getBottomAdjustment() {
        return rect.height - windowHeight;
      }

      switch (position) {
        case 'bottom':
          return getScrollResult(getBottomAdjustment());
        case 'top':
          return getScrollResult(getTopAdjustment());
        case 'minimum-onscreen':
          //cases:
          //element smaller than screen - check bottom and top, it will be above, below, or onscreen(no action needed)
          //element bigger than screen - align top of element to top of visible screen area

          const viewportHeight = windowHeight - headerHeight;
          if (rect.height < viewportHeight) {
            if (rect.top < headerHeight) {
              return getScrollResult(getTopAdjustment() - readabilityAdjustment);
            } else if (rect.bottom > windowHeight) {
              return getScrollResult(getBottomAdjustment() + readabilityAdjustment);
            } else {
              return noScrollResult;
            }
          } else if (rect.top === headerHeight + 1) {
            return noScrollResult;
          } else {
            return getScrollResult(getTopAdjustment());
          }
        default:
          return getScrollResult((rect.height / 2) - (windowHeight / 2));
      }
    }

    /**
     *
     * @param {Element } link element to scroll to
     * @param {string} position desired alignment of link on screen. Values: 'top', 'bottom', 'minimum-onscreen' (gets the element on screen
     * with minimal amount of scrolling).  Other values, including null, will  align link in the
     * middle of the screen
     * @param {number} offset additional offset to scroll link
     */
    calculateFullScroll(link, position, offset=0) {
      if (link.closest('.curtain-wrapper')) {
        return 0;
      }

      const currentScroll = this.getCurrentScroll();
      const rect = link.getBoundingClientRect();
      const adjustmentRes = this.calculateAdjustment(rect, position);
      if (!adjustmentRes.needsScroll) {
        return -1;
      }
      const adjustment = adjustmentRes.adjustment;
      const scrollY = rect.top + currentScroll + adjustment + offset;
      if (scrollY == currentScroll) {
        return -1;
      }
      const situationalOffset = this.getSituationalOffsetFromPos(scrollY);
      return Math.max(scrollY + situationalOffset, window.innerHeight);
    }

    /**
     * 
     * @param {Element } link element to scroll to
     * @param {string} position desired alignment of link on screen. Values: 'top', 'bottom', 'minimum-onscreen' (gets the element on screen
     * with minimal amount of scrolling).  Other values, including null, will  align link in the
     * middle of the screen
     * @param {number} offset additional offset to scroll link
     * @param {boolean} allowScrollUpdate if true, scroll handlers will not be informed that this scroll should be ignored.
     */
    scrollToElement(link,  position, offset = 0, allowScrollUpdate=false) {


      if (!allowScrollUpdate) {
        this.scrollUpdateChecker.setScrollUpdateAllowed(false);
      }

      const fullScroll = this.calculateFullScroll(link, position, offset);
     
      if (fullScroll >= 0) {
        window.scrollTo(0, fullScroll);
      }

      if (!allowScrollUpdate) {
        onScrollDone(() => this.scrollUpdateChecker.setScrollUpdateAllowed(true));
      }

    }

    /**
     *
     * @param {string} hash hash or id of element to scroll to
     * @param {Function} prepCallback function to call on id and element before scrolling, only if hash is valid.
     * @param {Function} doneCallback function to call when the scrolling is complete OR if the hash is invalid.
     * @param {boolean} allowScrollUpdate if true, scroll handlers will not be informed that this scroll should be ignored.
     */
    scrollToHash(hash, prepCallback, doneCallback, allowScrollUpdate, offset=0) {

      const doneFunc = doneCallback || function () { };



      if (!hash) {
        doneFunc();
        return null;
      }

      const elId = hash.replace(/^#+/, '');

      const target = document.getElementById(elId);
      if (!target) {
        doneFunc();
        return null;
      }

      if (prepCallback) {
        prepCallback(hash, target);
      }



      window.history.scrollRestoration = 'manual';

      /**
        * Scrolls to  the element, focuses on the element, .
        */
      const scrollToElem = () => {
        
        this.scrollToElement(target, 'top', offset, allowScrollUpdate);
        doneFunc();
      }
      scrollToElem();
      //setTimeout(scrollToElem, 1);
      return target;
    }

    /**Scrolls to the hash in the URL of the page
     *
     * @param {boolean} allowScrollUpdate if true, scroll handlers will not be informed that this scroll should be ignored.
    */
    scrollToUrlHash(allowScrollUpdate=false, defaultHash='', offset=0) {
      if (!window.location) {
        return;
      }

      const hash = window.location.hash || defaultHash;
      //const htmlEl = document.querySelector('html');
   
     
      this.scrollToHash(hash, window.location.hash ? (hash, el) => el.focus() : (hash, el) =>{}, null, allowScrollUpdate, offset);

    }


    /**
     * Adds an event that scrolls to URL hash when popstate is triggered.
     */
    subscribeToPopState() {
      window.addEventListener('popstate', e => {
        if (!(e.state && e.state.isTriggeredThroughCode)) {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => this.scrollToUrlHash(false), 10);
        }
      });
    }



}
